import { CDN_URL } from "@/config";
import axios from "axios";

const areaDataUrl = `${CDN_URL}map/areas.json`;

export interface AreaNode {
    id: string;
    name: string;
    shortName?: string;
    childs?: AreaNode[];
}
/**
 * 地区库
 * 数据源 https://github.com/zengzhan/qqzeng-ip/blob/master/mysql/area.sql
 * 淘宝店铺  qqzeng-ip
 */
class AreaLib {
    /** React 页面直接读取的地区数据，避免把 Vue Ref 泄漏到 React 运行时。 */
    public areaData: AreaNode[] = [];
    /** 地区数据加载完成后供 React 外部 store 订阅的版本号。 */
    public version = 0;
    /** 首次加载 Promise，表单或测试可等待地区数据准备完成。 */
    public readonly ready: Promise<void>;
    private readonly listeners = new Set<() => void>();

    constructor() {
        this.ready = this.loadAreaData();
    }

    /** 订阅地区数据变更，返回符合 React 外部 store 约定的取消订阅函数。 */
    subscribe = (listener: () => void) => {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    };

    /** 通知所有 React 页面重新读取地区数据。 */
    private notify() {
        this.version += 1;
        this.listeners.forEach(listener => listener());
    }

    /** 加载地区数据 */
    private async loadAreaData(): Promise<void> {
        try {
            const response = await axios.get(areaDataUrl);
            this.areaData = Array.isArray(response.data) ? response.data : [];
            this.notify();
        } catch (error) {
            console.error("Failed to load area data:", error);
        }
    }

    /** 获取子地区列表（同步） */
    getAreaList = (provinceAdcode?: string, cityAdcode?: string) => {
        if (!this.areaData.length) {
            return []; // 避免报错，返回空数组
        }

        let area = this.areaData;
        if (provinceAdcode) {
            area = (this.areaData.find(item => item.id === provinceAdcode)?.childs || []) as typeof area;
            if (cityAdcode) {
                area = (area.find(item => item.id === cityAdcode)?.childs || []) as typeof area;
            }
        }
        return area;
    };

    /** 获取只获取两层地区列表 */
    getAreaListEndCity = () => {
        if (!this.areaData.length) {
            return []; // 避免报错，返回空数组
        }
        return this.areaData.map(item => {
            if (item.childs?.length) {
                item.childs.forEach(child => {
                    delete child.childs;
                });
            }
            return item;
        });
    };

    /** 根据adcode获取地区名 */
    getAreaName = (adcode?: string, options: { provinceAdcode?: string; cityAdcode?: string } = {}) => {
        const { provinceAdcode, cityAdcode } = options;
        const area = this.getAreaList(provinceAdcode, cityAdcode);
        return area.find(item => item.id === adcode)?.name;
    };

    /** 获取完整省市区名 */
    getFullAreaName = (provinceAdcode?: string, cityAdcode?: string, regionAdcode?: string) => {
        let fullName = "";
        if (provinceAdcode) {
            fullName += this.getAreaName(provinceAdcode) ?? "";
            if (cityAdcode) {
                fullName += this.getAreaName(cityAdcode, { provinceAdcode }) ?? "";
                if (regionAdcode) {
                    fullName += this.getAreaName(regionAdcode, { provinceAdcode, cityAdcode }) ?? "";
                }
            }
        }
        return fullName;
    };

    /** 根据adcode获取简称 */
    getAreaShortName = (adcode?: string, options: { provinceAdcode?: string; cityAdcode?: string } = {}) => {
        const { provinceAdcode, cityAdcode } = options;
        const area = this.getAreaList(provinceAdcode, cityAdcode);
        return area.find(item => item.id === adcode)?.shortName;
    };

    /** 获取完整省市简称 */
    getShortAreaName = (provinceAdcode: string, cityAdcode?: string, regionAdcode?: string, last: boolean = false) => {
        const provinceName = this.getAreaShortName(provinceAdcode) ?? "";
        const cityName = this.getAreaShortName(cityAdcode, { provinceAdcode }) ?? "";
        const regionName = this.getAreaShortName(regionAdcode, { provinceAdcode, cityAdcode }) ?? "";

        let fullName = provinceName;
        if (last) {
            fullName = regionName || cityName;
        } else {
            if (cityName) fullName += cityName;
            if (regionName) fullName += regionName;
        }
        // 定义直辖市名称列表
        const cities = ["北京", "上海", "天津", "重庆"];
        // 遍历每个直辖市名称并替换重复的部分
        cities.forEach(city => {
            const regex = new RegExp(`(${city})\\1`, "g"); // 正则表达式匹配重复的城市名称
            fullName = fullName.replace(regex, "$1"); // 将重复的城市名称替换为单个
        });
        return fullName;
    };

    /** areaLib列表转options列表 */
    areaList2Options = (areaList: AreaNode[]) => {
        type Option = {
            label: string;
            value: any;
            children?: Option[];
        };
        return areaList.map(item => {
            const option: Option = {
                label: item.name,
                value: item.id
            };
            if (item.childs) {
                option.children = this.areaList2Options(item.childs);
            }
            return option;
        });
    };
    /**
     * areaLib 列表添加一个disable 属性
     * */
    areaListAddDisabled = (areaList: AreaNode[]) => {
        type Option = {
            id: string;
            name: unknown;
            disabled: boolean;
            children?: Option[];
        };
        return areaList.map(item => {
            const option: Option = {
                ...item,
                disabled: false
            };
            if (item.childs) {
                option.children = this.areaListAddDisabled(item.childs);
            }
            return option;
        });
    };

    /**
     * 获取城市名称
     * @param cityAdcode
     */
    getCityNameByCode = (cityAdcode: string) => {
        if (!this.areaData.length) return "";
        const cityList = this.areaData.flatMap(item => item.childs || []);
        return cityList.find(item => item.id === cityAdcode)?.name || "";
    };
}

export const areaLib = new AreaLib();
